import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import toSentenceCase from 'helpers/sentenceCase';
import styles from './JobBreadcrumb.module.scss';

/**
 * Crumbs for the freelancer job page.
 * Logged-in visitors see "Jobs" (same auth signal as the header: a user object).
 * Guests see "Find freelance jobs". BreadcrumbList JSON-LD is not emitted here;
 * there is none on this page to keep in step.
 */
export function getJobBreadcrumbSteps({
  user,
  owner,
  brief,
  city,
  country,
  locationLink,
}) {
  const loggedIn = Boolean(user);
  const isOwner = Boolean(loggedIn && owner && user.id === owner.id);
  const roleLabel = toSentenceCase(brief && brief.role);
  const place = city || country;
  const paid = Boolean(brief && brief.amount && brief.amount !== 0);
  const steps = [];

  if (isOwner) {
    steps.push({ href: '/ats', label: 'Manage Jobs' });
  } else {
    steps.push({
      href: '/jobs',
      label: loggedIn ? 'Jobs' : 'Find freelance jobs',
    });

    if (paid && brief.role) {
      steps.push({
        href: `/jobs/${toSeoUrl(toPlural(brief.role))}`,
        label: `${roleLabel} jobs`,
      });
    }

    if (paid && brief.location && place) {
      steps.push({
        href: locationLink,
        label: `${roleLabel} jobs in ${place}`,
      });
    }
  }

  steps.push({ label: 'Job details', current: true });
  return steps;
}

const JobBreadcrumb = (props) => {
  const steps = getJobBreadcrumbSteps(props);

  return (
    <nav aria-label="Breadcrumb" className={`mb++ ${styles.jobBreadcrumb}`}>
      <ol className={styles.list}>
        {steps.map((step, index) => (
          <li
            key={`${step.label}-${index}`}
            className={styles.step}
            aria-current={step.current ? 'page' : undefined}
          >
            {index > 0 && (
              <span className={styles.separator} aria-hidden="true">
                ›
              </span>
            )}
            {step.href && !step.current ? (
              <Link to={step.href}>{step.label}</Link>
            ) : (
              <span className={styles.current}>{step.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

JobBreadcrumb.propTypes = {
  user: PropTypes.object,
  owner: PropTypes.object,
  brief: PropTypes.object,
  city: PropTypes.string,
  country: PropTypes.string,
  locationLink: PropTypes.string,
};

export default JobBreadcrumb;
